<template>
  <div id="input-dividends">
    <v-card id="input-dividends-card" class="px-5 py-2 mt-4 mb-5 elevation-0">
      <div :class="{ 'sticky-filters': user.preferences.stickyFilters }">
        <CommonFilters ref="commonFilters" type="dividend" @filterElements="filterElements">
          <template v-slot:add-button>
            <AddButtons type="dividend" @clicked="createDividend" />
            <template v-if="!isMobile">
              <ImportButton :click="openImportModal" />
            </template>
            <MoreButtons :buttons="moreButtons" :loading="exportLoading" />
          </template>
        </CommonFilters>
      </div>
      <v-data-table
        :headers="headers"
        :items="items"
        :options.sync="options"
        :server-items-length="totalItems"
        sort-by="date"
        :sort-desc="true"
        class="input-dividends-table elevation-0 mt-2"
        mobile-breakpoint="0"
        :loading="loading"
        :no-data-text="$t('views.dividends.dividendTable.noDividends')"
        :item-class="itemRowBackground"
        :footer-props="{
          'items-per-page-options': defaultPaginationItemsPerPageOptions,
        }"
        :items-per-page="user.preferences.denseTables ? 15 : 10"
        :dense="user.preferences.denseTables"
        @update:options="updateOptions"
      >
        <template v-slot:item.type="{ item }">
          <DividendTableTypeIcon :item="item" />
        </template>
        <template v-slot:item.date="{ item }">
          {{ getDateUserFormat(item.date) }}
        </template>
        <template v-slot:item.broker.alias="{ item }">
          <TableBroker :broker="item.broker" />
        </template>
        <template v-slot:item.position.ticker="{ item }">
          <TableTicker :position="item.position" />
        </template>
        <template v-slot:item.positionNumber="{ item }">
          <currency v-if="item.positionNumber" :value="item.positionNumber" :decimals="7" />
          <span v-else></span>
        </template>
        <template v-slot:item.quantity="{ item }">
          <currency :value="item.quantity" :currency="item.quantityCurrency" :decimals="3" />
        </template>
        <template v-slot:item.total="{ item }">
          <currency v-if="item.total" :value="item.total" :currency="item.currency" />
          <span v-else></span>
        </template>
        <template v-slot:item.exchangeRate="{ item }">
          <TableExchangeRate :item="item" type="dividend" @edit="editDividend(item)" />
        </template>
        <template v-slot:item.totalBaseCurrency="{ item }">
          <currency
            v-if="item.totalBaseCurrency"
            :value="item.totalBaseCurrency"
            :currency="user.baseCurrency"
            strong
          />
          <span v-else></span>
        </template>
        <template v-slot:item.originRetention="{ item }">
          <div class="items-flex justify-end">
            <currency
              v-if="item.originRetention || item.customOriginRetention"
              :value="item.originRetention"
              :currency="item.quantityCurrency"
            />
            <span v-else></span>
            <template v-if="item.customOriginRetention">
              <v-tooltip top max-width="300px">
                <template v-slot:activator="{ on, attrs }">
                  <v-avatar v-bind="attrs" size="7" color="accentYellow" class="ml-1" v-on="on" />
                </template>
                <span>{{ $t('views.dividends.dividendTable.customOriginRetention') }}</span>
              </v-tooltip>
            </template>
          </div>
        </template>

        <template v-slot:item.neto="{ item }">
          <currency v-if="item.neto" :value="item.neto" :currency="item.currency" />
          <span v-else></span>
        </template>
        <template v-slot:item.netoBaseCurrency="{ item }">
          <currency
            v-if="item.netoBaseCurrency"
            :value="item.netoBaseCurrency"
            :currency="user.baseCurrency"
          />
          <span v-else></span>
        </template>

        <template v-slot:item.destinationRetentionBaseCurrency="{ item }">
          <div class="items-flex justify-end">
            <currency
              v-if="item.destinationRetentionBaseCurrency || item.customDestinationRetention"
              :value="item.destinationRetentionBaseCurrency"
              :currency="user.baseCurrency"
            />
            <span v-else></span>
            <template v-if="item.customDestinationRetention">
              <v-tooltip top max-width="300px">
                <template v-slot:activator="{ on, attrs }">
                  <v-avatar v-bind="attrs" size="7" color="accentYellow" class="ml-1" v-on="on" />
                </template>
                <span>{{ $t('views.dividends.dividendTable.customDestinationRetention') }}</span>
              </v-tooltip>
            </template>
          </div>
        </template>

        <template v-slot:item.comission="{ item }">
          <currency
            v-if="item.comission"
            :value="item.comission"
            :currency="item.comissionCurrency"
            :text-red="!!item.comission"
            :decimals="5"
          />
          <span v-else></span>
        </template>

        <template v-slot:item.comissionBaseCurrency="{ item }">
          <currency
            v-if="item.comissionBaseCurrency"
            :value="item.comissionBaseCurrency"
            :currency="user.baseCurrency"
            :text-red="!!item.comissionBaseCurrency"
          />
          <span v-else></span>
        </template>

        <template v-slot:item.totalNetoBaseCurrency="{ item }">
          <currency
            v-if="item.totalNetoBaseCurrency"
            :value="item.totalNetoBaseCurrency"
            :currency="user.baseCurrency"
          />
          <span v-else></span>
        </template>

        <template v-slot:item.retentionReturned="{ item }">
          <currency
            v-if="item.retentionReturned"
            :value="item.retentionReturned"
            :currency="item.currency"
          />
          <span v-else></span>
        </template>
        <template v-slot:item.retentionReturnedBaseCurrency="{ item }">
          <currency
            v-if="item.retentionReturnedBaseCurrency"
            :value="item.retentionReturnedBaseCurrency"
            :currency="user.baseCurrency"
          />
          <span v-else></span>
        </template>

        <template v-slot:item.unrealizedDestinationRetentionBaseCurrency="{ item }">
          <currency
            v-if="item.unrealizedDestinationRetentionBaseCurrency"
            :value="item.unrealizedDestinationRetentionBaseCurrency"
            :currency="user.baseCurrency"
            :text-green="item.unrealizedDestinationRetentionBaseCurrency < 0"
            :text-red="item.unrealizedDestinationRetentionBaseCurrency > 0"
            :negative="item.unrealizedDestinationRetentionBaseCurrency < 0"
          />
          <span v-else></span>
        </template>

        <template v-slot:item.netoWithReturnBaseCurrency="{ item }">
          <currency
            v-if="item.netoWithReturnBaseCurrency"
            :value="item.netoWithReturnBaseCurrency"
            :currency="user.baseCurrency"
            strong
          />
          <span v-else></span>
        </template>

        <template v-slot:item.originRetentionLossBaseCurrency="{ item }">
          <currency
            v-if="item.originRetentionLossBaseCurrency"
            :value="item.originRetentionLossBaseCurrency"
            :currency="user.baseCurrency"
            text-red
          />
          <span v-else></span>
        </template>

        <template v-slot:item.autoFx="{ item }">
          <div v-if="item.currency !== user.baseCurrency">
            <v-icon v-if="item.autoFx">mdi-toggle-switch</v-icon>
            <v-icon v-else>mdi-toggle-switch-off-outline</v-icon>
          </div>
          <span v-else></span>
        </template>

        <template v-slot:item.actions="{ item }">
          <template v-if="item.description">
            <v-tooltip top max-width="300px">
              <template v-slot:activator="{ on, attrs }">
                <v-icon
                  color="secondary"
                  v-bind="attrs"
                  small
                  class="mr-4"
                  style="margin-top: 3px;"
                  v-on="on"
                >
                  mdi-message-bulleted
                </v-icon>
              </template>
              <span>{{ item.description }}</span>
            </v-tooltip>
          </template>

          <v-tooltip top>
            <template v-slot:activator="{ on, attrs }">
              <v-icon
                v-bind="attrs"
                size="16"
                class="mr-2"
                v-on="on"
                @click="duplicateDividend(item)"
              >
                mdi-content-copy
              </v-icon>
            </template>
            <span>
              {{
                item.type === 'stockDividend'
                  ? $t('views.dividends.dividendTable.duplicateDividend')
                  : $t('views.dividends.dividendTable.duplicateCoupon')
              }}
            </span>
          </v-tooltip>
          <v-tooltip top>
            <template v-slot:activator="{ on, attrs }">
              <v-icon v-bind="attrs" size="18" class="mr-2" v-on="on" @click="editDividend(item)">
                mdi-pencil-outline
              </v-icon>
            </template>
            <span>{{ $t('common.edit') }}</span>
          </v-tooltip>
          <v-tooltip top>
            <template v-slot:activator="{ on, attrs }">
              <v-icon v-bind="attrs" size="18" v-on="on" @click="deleteDividend(item)">
                mdi-delete-outline
              </v-icon>
            </template>
            <span>{{ $t('common.delete') }}</span>
          </v-tooltip>
        </template>
      </v-data-table>

      <DividendModal
        :isModalActive.sync="isDividendModalActive"
        :typeDividendModal="typeDividendModal"
        :editItem="editItem"
        :editing="editing"
        :duplicating="duplicating"
        @saved="(obj) => loadItems({ ...obj })"
      />
      <ImportModal :isModalActive.sync="isImportModalActive" type="dividend" @init="loadItems" />
      <HeadersModal
        v-if="isHeadersModalActive"
        :isModalActive.sync="isHeadersModalActive"
        :tableName="tableName"
        :defaultHeaders="defaultHeaders"
      />
    </v-card>
  </div>
</template>

<script>
import { DateTime } from 'luxon'
import FileSaver from 'file-saver'
import { mapState } from 'vuex'
import { get } from 'lodash'
import { getDividends, deleteDividend, exportDividendsCsv } from '@/lib/api.js'
import { getDateUserFormat } from '@/lib/utils/date.js'
import { getCurrencySymbol } from '@/lib/utils/currencies.js'
import tableMixin from '@/views/components/core/tableMixin.js'
import CommonFilters from '@/views/components/common/CommonFilters'
import DividendTableTypeIcon from '@/views/components/common/dividends/DividendTableTypeIcon'
import FilterButton from '@/views/components/common/FilterButton'
import Currency from '@/views/components/common/Currency.vue'
import AddButtons from '@/views/components/common/AddButtons'
import MoreButtons from '@/views/components/common/MoreButtons'
import TableTicker from '@/views/components/common/TableTicker'
import TableBroker from '@/views/components/common/TableBroker'
import TableExchangeRate from '@/views/components/common/TableExchangeRate'
import DividendModal from '@/views/components/common/dividends/DividendModal'
import ImportButton from '@/views/components/imports/ImportButton'
import ImportModal from '@/views/components/imports/ImportModal'
import gtag from '@/lib/gtag'

export default {
  components: {
    FilterButton,
    CommonFilters,
    DividendTableTypeIcon,
    Currency,
    AddButtons,
    MoreButtons,
    TableTicker,
    TableBroker,
    TableExchangeRate,
    DividendModal,
    ImportButton,
    ImportModal,
  },
  mixins: [tableMixin],
  data: () => ({
    exportLoading: false,
    filters: {},
    editing: false,
    isDividendModalActive: false,
    isImportModalActive: false,
    editItem: {},
    duplicating: false,
    typeDividendModal: null,
    tableName: 'dividends',
  }),
  computed: {
    ...mapState(['user']),
    isMobile() {
      return this.$vuetify.breakpoint.xs
    },
    defaultHeaders() {
      return [
        {
          text: '',
          value: 'type',
          customSecondaryText: this.$t('views.dividends.dividendTable.type'),
          sortable: false,
        },
        { text: this.$t('views.dividends.dividendTable.date'), value: 'date' },
        {
          text: this.$t('views.dividends.dividendTable.broker'),
          value: 'broker.alias',
          sortable: false,
        },
        {
          text: this.$t(`views.dividends.dividendTable.position`),
          value: 'position.ticker',
          sortable: false,
        },
        {
          text: this.$t(`views.dividends.dividendTable.positionNumber`),
          value: 'positionNumber',
          align: 'right',
        },
        {
          text: this.$t(`views.dividends.dividendTable.quantity`),
          value: 'quantity',
          align: 'right',
        },
        { text: this.$t('views.dividends.dividendTable.total'), value: 'total', align: 'right' },
        {
          text: this.$t('views.dividends.dividendTable.exchangeRate'),
          value: 'exchangeRate',
          align: 'right',
        },
        {
          text: `${this.$t('views.dividends.dividendTable.total')} (${getCurrencySymbol(
            this.user.baseCurrency
          )})`,
          value: 'totalBaseCurrency',
          align: 'right',
        },
        {
          text: this.$t('views.dividends.dividendTable.originRetention'),
          value: 'originRetention',
          align: 'right',
        },
        { text: this.$t('views.dividends.dividendTable.neto'), value: 'neto', align: 'right' },
        {
          text: `${this.$t('views.dividends.dividendTable.neto')} (${getCurrencySymbol(
            this.user.baseCurrency
          )})`,
          value: 'netoBaseCurrency',
          align: 'right',
        },
        {
          text: `${this.$t(
            'views.dividends.dividendTable.destinationRetention'
          )} (${getCurrencySymbol(this.user.baseCurrency)})`,
          value: 'destinationRetentionBaseCurrency',
          align: 'right',
        },
        {
          text: `${this.$t('views.dividends.dividendTable.comission')}`,
          value: 'comission',
          align: 'right',
        },
        {
          text: `${this.$t('views.dividends.dividendTable.comission')} (${getCurrencySymbol(
            this.user.baseCurrency
          )})`,
          value: 'comissionBaseCurrency',
          align: 'right',
          customHidden: true,
        },
        {
          text: `${this.$t('views.dividends.dividendTable.totalNeto')} (${getCurrencySymbol(
            this.user.baseCurrency
          )})`,
          value: 'totalNetoBaseCurrency',
          align: 'right',
        },
        {
          text: this.$t('views.dividends.dividendTable.retentionReturned'),
          value: 'retentionReturned',
          align: 'right',
        },
        {
          text: `${this.$t('views.dividends.dividendTable.retentionReturned')} (${getCurrencySymbol(
            this.user.baseCurrency
          )})`,
          value: 'retentionReturnedBaseCurrency',
          align: 'right',
        },
        {
          text: `${this.$t(
            'views.dividends.dividendTable.unrealizedDestinationRetention'
          )} (${getCurrencySymbol(this.user.baseCurrency)})`,
          value: 'unrealizedDestinationRetentionBaseCurrency',
          align: 'right',
        },
        {
          text: `${this.$t('views.dividends.dividendTable.netoWithReturn')} (${getCurrencySymbol(
            this.user.baseCurrency
          )})`,
          value: 'netoWithReturnBaseCurrency',
          align: 'right',
        },
        {
          text: `${this.$t(
            'views.dividends.dividendTable.originRetentionLoss'
          )} (${getCurrencySymbol(this.user.baseCurrency)})`,
          value: 'originRetentionLossBaseCurrency',
          align: 'right',
        },
        {
          text: this.$t('views.dividends.dividendTable.autoFx'),
          value: 'autoFx',
          align: 'right',
          sortable: false,
        },
        {
          text: '',
          value: 'actions',
          sortable: false,
          align: 'right',
          customHideDisabled: true,
          customSecondaryText: this.$t('views.dividends.dividendTable.editRemove'),
        },
      ]
    },
    moreButtons() {
      const moreButtons = []
      if (this.isMobile) {
        moreButtons.push({
          value: 'import',
          title: this.$t('views.common.moreButtons.import'),
          icon: 'mdi-file-upload-outline',
          action: this.openImportModal,
        })
      }
      moreButtons.push({
        value: 'download',
        title: this.$t('views.common.moreButtons.download'),
        icon: 'mdi-cloud-download-outline',
        action: this.downloadCsv,
        disabled: !this.items.length,
      })
      moreButtons.push({
        value: 'customHeaders',
        title: this.$t('views.common.headersModal.title'),
        icon: 'mdi-cog-outline',
        action: this.customHeaders,
      })
      return moreButtons
    },
  },
  methods: {
    getDateUserFormat,
    createDividend(type) {
      this.editing = false
      this.duplicating = false
      this.isDividendModalActive = true
      this.typeDividendModal = type

      gtag.pageview({
        pagePath: `/input/dividends/create/${this.typeDividendModal}`,
        pageTitle: `input|dividends|create|${this.typeDividendModal}`,
        pageContentGroup: 'modal',
      })
    },
    editDividend(dividend) {
      this.editing = true
      this.duplicating = false
      this.isDividendModalActive = true
      this.typeDividendModal = dividend.type
      this.editItem = JSON.parse(JSON.stringify(dividend))

      gtag.pageview({
        pagePath: `/input/dividends/edit/${this.typeDividendModal}`,
        pageTitle: `input|dividends|edit|${this.typeDividendModal}`,
        pageContentGroup: 'modal',
      })
    },
    duplicateDividend(dividend) {
      this.editing = false
      this.duplicating = true
      this.isDividendModalActive = true
      this.typeDividendModal = dividend.type
      this.editItem = JSON.parse(JSON.stringify(dividend))

      gtag.pageview({
        pagePath: `/input/dividends/duplicate/${this.typeOrderModal}`,
        pageTitle: `input|dividends|duplicate|${this.typeOrderModal}`,
        pageContentGroup: 'modal',
      })
    },
    itemRowBackground(item) {
      if (item.exchangeRate === null) return 'row-advise'
    },
    async loadItems() {
      this.loading = true
      try {
        const filter = JSON.parse(JSON.stringify(this.filters))
        const { page, limit, sort } = this.getTablePaginationOptions(this.options)
        const response = await getDividends({ filter, page, limit, sort })
        this.setTablePaginatedResponse(response)
      } catch (error) {
        this.$root.$snackbar.checkAndOpenCommonLoadError(error)
      }
      this.loading = false
    },
    async deleteDividend(dividend) {
      if (
        await this.$root.$confirm({
          title: this.$t('views.dividends.dividendTable.confirm.title'),
          message: this.$t('views.dividends.dividendTable.confirm.message'),
        })
      ) {
        this.loading = true
        try {
          await deleteDividend(dividend)
          await this.loadItems()
          this.$root.$snackbar.success({ text: this.$t('common.deleteOk') })

          gtag.pageview({
            pagePath: `/input/dividends/delete/success`,
            pageTitle: `input|dividends|delete|success`,
            pageContentGroup: 'modal',
          })
        } catch (error) {
          this.$root.$snackbar.error({ text: this.$t('common.deleteError') })

          gtag.pageview({
            pagePath: `/input/dividends/delete/error`,
            pageTitle: `input|dividends|delete|error`,
            pageContentGroup: 'modal',
          })
        }
        this.loading = false
      }
    },
    async downloadCsv() {
      this.exportLoading = true
      const nowFormatted = DateTime.now().toFormat('yyyy_MM_dd HH_mm_ss')
      try {
        const result = await exportDividendsCsv({ filter: this.filters })
        const blob = new Blob([result.data], { type: 'text/csv;charset=utf-8' })
        FileSaver.saveAs(
          blob,
          `Filios - ${this.$t('views.input.tabs.dividends')} ${nowFormatted}.csv`
        )

        gtag.pageview({
          pagePath: `/input/dividends/export/success`,
          pageTitle: `input|dividends|export|success`,
          pageContentGroup: 'modal',
        })
      } catch (error) {
        const errorCode = get(error, 'data.name', null)
        if (errorCode === 'dividends.limitReached') {
          this.$root.$snackbar.error({
            text: this.$t('views.dividends.dividendTable.limitReached'),
            timeout: 10000,
          })
        } else {
          this.$root.$snackbar.checkAndOpenCommonLoadError(error)
        }

        gtag.pageview({
          pagePath: `/input/dividends/export/error`,
          pageTitle: `input|dividends|export|error`,
          pageContentGroup: 'modal',
        })
      }

      this.exportLoading = false
    },
    openImportModal() {
      this.isImportModalActive = true

      gtag.pageview({
        pagePath: `/input/dividends/import`,
        pageTitle: `input|dividends|import`,
        pageContentGroup: 'modal',
      })
    },
  },
}
</script>

<style lang="scss">
#input-dividends {
  #input-dividends-card {
    .items-flex {
      display: flex;
      align-items: center;
      &.justify-end {
        justify-content: flex-end;
      }
    }
  }
}
</style>
